import React, { useEffect, useRef, useState } from "react";
import "./ElderHomePage.css";
import Navbar from "../common/Navbar";
import Footer from "../common/Footer";
import ComingSoonModal from "../common/ComingSoonModal";
import DownloadAppModal from "../common/DownloadAppModal";
import WaitlistForm from "../common/WaitlistForm";
import { COVERAGE_CITIES } from "../../data/coverage";
import heroImage from "../assets/hero.png";
import whyImage from "../assets/ELC/shopping.png";
import testimonial1 from "../assets/ELC/normal-visit.png";
import testimonial2 from "../assets/ELC/posting.png";
import testimonial3 from "../assets/ELC/airport-1.png";

const faqs = [
  {
    q: "Who are the companions?",
    a: "Fully background-verified young companions, trained to spend time with elders. They know how to talk, listen and be patient — so your loved one feels comfortable, respected and never rushed.",
  },
  {
    q: "Are they medical staff?",
    a: "No. EldyCare companions are not doctors or nurses. They provide company, help with visits and errands, and emotional support — not medical treatment.",
  },
  {
    q: "Can I stay on a call during the visit?",
    a: "Yes. Families can stay connected by phone during a session so you know how your loved one is doing.",
  },
  {
    q: "How do I cancel?",
    a: "Cancellations and reschedules will be handled in the EldyCare app once it launches. Until then, WhatsApp or call us on 8595729839.",
  },
  {
    q: "Is booking only on the app?",
    a: "Yes. Companions are booked in the EldyCare app, not on this website. The website is for information and waitlist sign-up.",
  },
  {
    q: "Where do you operate?",
    a: "We are launching in Noida. If you need EldyCare in Delhi, Gurugram, Faridabad, Nainital, Haridwar, Rishikesh, Vaishno Devi or another city, leave your email and pincode and we’ll tell you when we reach your area.",
  },
  {
    q: "How much does it cost?",
    a: "Sessions start from ₹299 per hour. Final hourly rates are shown in the app before you book, so pricing stays transparent.",
  },
  {
    q: "What if my parent is not comfortable?",
    a: "Tell us and we will assign a different companion. Comfort comes first — no one is expected to continue a session that does not feel right.",
  },
  {
    q: "How long is a session?",
    a: "You book by the hour, based on what is needed that day — a short walk, a hospital visit, or a few hours of company. The duration is chosen in the app before you confirm.",
  },
];

const ElderHomePage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [requestedCity, setRequestedCity] = useState("");
  const [openFaq, setOpenFaq] = useState(0);
  const [heroProgress, setHeroProgress] = useState(0);
  const heroRef = useRef(null);
  const combinedTrackRef = useRef(null);
  const combinedCardsRef = useRef([]);
  const openModal = () => setModalOpen(true);
  const openDownload = () => setDownloadOpen(true);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (media.matches) {
      setHeroProgress(1);
      return undefined;
    }

    let frame = 0;

    const update = () => {
      const el = heroRef.current;
      if (!el) {
        return;
      }

      const range = el.offsetHeight - window.innerHeight;
      const raw = range > 0 ? -el.getBoundingClientRect().top / range : 1;
      setHeroProgress(Math.min(1, Math.max(0, raw)));
    };

    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  useEffect(() => {
    const paintCard = (el, t) => {
      if (!el) {
        return;
      }

      const progress = Math.min(1, Math.max(0, t));
      const rest = 1 - progress;
      el.style.opacity = String(progress);
      el.style.transform = `translate3d(0, ${rest * 120}px, 0) rotateX(${rest * -16}deg) scale(${0.88 + progress * 0.12})`;
    };

    const showAll = () => {
      combinedCardsRef.current.forEach((el) => paintCard(el, 1));
    };

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (reduced.matches) {
      showAll();
      return undefined;
    }

    let frame = 0;

    const update = () => {
      const track = combinedTrackRef.current;
      const cards = combinedCardsRef.current;
      if (!track || !cards.length) {
        return;
      }

      const viewport = window.innerHeight;

      if (window.innerWidth <= 900) {
        cards.forEach((el) => {
          const top = el.getBoundingClientRect().top;
          const local = (viewport * 0.88 - top) / (viewport * 0.28);
          const eased = 1 - (1 - Math.min(1, Math.max(0, local))) ** 3;
          paintCard(el, eased);
        });
        return;
      }

      const rect = track.getBoundingClientRect();
      const start = viewport * 0.82;
      const travel = Math.max(1, track.offsetHeight - viewport * 0.35);
      const overall = (start - rect.top) / travel;
      const span = 0.26;
      const step = (1 - span) / Math.max(1, cards.length - 1);

      cards.forEach((el, index) => {
        const local = (overall - index * step) / span;
        const eased = 1 - (1 - Math.min(1, Math.max(0, local))) ** 3;
        paintCard(el, eased);
      });
    };

    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const reveal = Math.min(1, Math.max(0, heroProgress / 0.55));
  const eased = 1 - (1 - reveal) ** 3;
  const blur = (eased * 18).toFixed(2);
  const scale = (1 + eased * 0.08).toFixed(3);
  const veil = (eased * 0.48).toFixed(3);
  const glass = (0.28 + eased * 0.44).toFixed(3);
  const slide = (1 - eased).toFixed(3);
  const copyOpacity = eased.toFixed(3);

  return (
    <div className="elder-home">
      <Navbar onGetApp={openModal} onServices={openDownload} overHero={heroProgress < 0.5} />

      <section className="hero-section" id="home" ref={heroRef}>
        <div className="hero-sticky">
          <img
            className="hero-bg"
            src={heroImage}
            alt="EldyCare companion walking with an elder in a park"
            style={{
              filter: `blur(${blur}px)`,
              transform: `scale(${scale})`,
            }}
          />
          <div className="hero-veil" style={{ opacity: veil }} />
          <div
            className="hero-copy"
            style={{
              opacity: copyOpacity,
              transform: `translate3d(0, calc(${slide} * 72vh), 0)`,
              pointerEvents: eased > 0.2 ? "auto" : "none",
            }}
          >
            <div
              className="hero-glass"
              style={{
                background: `rgba(246, 241, 232, ${glass})`,
              }}
            >
              <span className="hero-badge">Trusted elder companionship</span>
              <h1>
                Meaningful human
                <br />
                connection for
                <br />
                your loved ones
              </h1>
              <p className="hero-lead">
                Verified companions for hospital visits, walks, conversations and daily assistance.
              </p>
              <div className="hero-actions">
                <button className="btn-primary" type="button" onClick={openModal}>
                  Get the app
                </button>
                <a className="btn-text" href="#how-it-works">
                  See how it works
                </a>
              </div>
              <p className="hero-stats">
                Verified companions · 98% family satisfaction · App coming soon
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="services-section" id="services">
        <h2>How EldyCare Helps</h2>

        <div className="services-grid">
          {[
            { icon: "🏥", title: "Hospital Visits", text: "Trusted companions for doctor visits, checkups and medical support." },
            { icon: "🚶", title: "Walks & Exercise", text: "Daily walks, park visits and healthy outdoor activities." },
            { icon: "☕", title: "Companionship", text: "Meaningful conversations and emotional support for elders." },
            { icon: "🛍️", title: "Shopping Help", text: "Assistance with groceries, errands and local shopping." },
          ].map((service) => (
            <article
              key={service.title}
              className="service-card"
              role="button"
              tabIndex={0}
              onClick={openDownload}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openDownload();
                }
              }}
            >
              <div className="service-icon" aria-hidden="true">{service.icon}</div>
              <h3>{service.title}</h3>
              <p>{service.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="steps-section" id="how-it-works">
        <div className="steps-header">
          <h2>How EldyCare Works</h2>
          <p>Simple, trusted and designed for families.</p>
        </div>

        <div className="steps-grid">
          <article className="step-card">
            <div className="step-number">01</div>
            <h3>Download the app</h3>
            <p>Get EldyCare from the store when it launches. Booking lives in the app, not on this website.</p>
          </article>
          <article className="step-card">
            <div className="step-number">02</div>
            <h3>Choose a companion</h3>
            <p>Browse verified companions based on availability and the kind of support you need.</p>
          </article>
          <article className="step-card">
            <div className="step-number">03</div>
            <h3>Stay connected</h3>
            <p>Receive updates and stories after every hospital visit, walk or conversation.</p>
          </article>
        </div>
      </section>

      <section className="split-section pricing-section" id="pricing">
        <div className="split-copy">
          <p className="section-kicker">Pricing</p>
          <h2>Simple hourly care</h2>
          <p className="section-lead">
            Transparent rates, shown in the app before you book. No website checkout.
          </p>
        </div>
        <div className="split-card pricing-card">
          <p className="pricing-from">From</p>
          <p className="pricing-amount">₹299<span>/hour</span></p>
          <p className="pricing-note">
            Hospital visits, walks, companionship and shopping help. Exact price for each companion and slot is visible in the EldyCare app.
          </p>
          <ul className="pricing-points">
            <li>Pay only for the hours you book</li>
            <li>No hidden joining fee on the website</li>
            <li>Final amount confirmed in the app</li>
          </ul>
        </div>
      </section>

      <section className="split-section coverage-section" id="coverage">
        <div className="split-copy">
          <p className="section-kicker">Coverage</p>
          <h2>Launching in Noida</h2>
          <p className="section-lead">
            EldyCare is starting in Noida. Delhi, Gurugram, Faridabad, Nainital, Haridwar, Rishikesh and Vaishno Devi are next — tell us if you want us there.
          </p>
          <div className="city-list">
            {COVERAGE_CITIES.map((item) => (
              <button
                key={item.name}
                type="button"
                className={`city-chip${item.status === "live" ? " city-chip-live" : ""}${
                  requestedCity === item.name ? " city-chip-selected" : ""
                }`}
                onClick={() => setRequestedCity(item.name)}
              >
                <strong>{item.name}</strong>
                <span>{item.status === "live" ? "Live soon" : "Request access"}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="split-card coverage-form-card">
          <h3>Want this in your city?</h3>
          <p>
            Choose your city, then add your email and pincode. We’ll keep count of every request and notify you when we launch there.
          </p>
          <WaitlistForm
            submitLabel="Notify me for my city"
            selectedCity={requestedCity}
            onCityChange={setRequestedCity}
          />
        </div>
      </section>

      <section className="why-section" id="why">
        <div className="why-left">
          <img src={whyImage} alt="EldyCare companion shopping with elders" />
        </div>
        <div className="why-right">
          <h2>Why Families Trust EldyCare</h2>
          <ul className="why-points">
            <li>Verified companions</li>
            <li>Real-time support</li>
            <li>Affordable hourly care</li>
            <li>Emotional & social wellbeing</li>
          </ul>
        </div>
      </section>

      <section className="testimonials-section" id="stories">
        <div className="testimonials-header">
          <p className="section-kicker">Everyday support</p>
          <h2>There when it matters</h2>
          <p className="testimonials-lead">
            Company at home, help with the day’s tasks, and a familiar face at the airport.
          </p>
        </div>
        <div className="testimonials-grid">
          <article className="testimonial-card">
            <img src={testimonial1} alt="Companion spending time with an elder" className="testimonial-portrait" />
            <h3>Companionship</h3>
            <p>A familiar face at home for conversation, company and quiet everyday moments.</p>
          </article>
          <article className="testimonial-card">
            <img src={testimonial2} alt="Companion assisting an elder at the post office" />
            <h3>Everyday errands</h3>
            <p>Help at the post office and with other daily tasks, so outings feel simpler.</p>
          </article>
          <article className="testimonial-card">
            <img src={testimonial3} alt="Companion picking up an elder at the airport" />
            <h3>Airport pickup</h3>
            <p>A trusted hand from arrival to home, so travel feels safe and calm.</p>
          </article>
        </div>
      </section>

      <section className="faq-section" id="faq">
        <p className="section-kicker">FAQ</p>
        <h2>Questions families ask</h2>
        <div className="faq-list">
          {faqs.map((item, index) => {
            const open = openFaq === index;
            return (
              <article className={`faq-item ${open ? "open" : ""}`} key={item.q}>
                <button
                  type="button"
                  className="faq-question"
                  aria-expanded={open}
                  onClick={() => setOpenFaq(open ? -1 : index)}
                >
                  {item.q}
                  <span aria-hidden="true">{open ? "−" : "+"}</span>
                </button>
                {open ? <p className="faq-answer">{item.a}</p> : null}
              </article>
            );
          })}
        </div>
      </section>

      <section className="combined-section">
        <div className="content-wrapper">
          <div className="combined-left">
            <span className="subtitle">Premium Companion Care</span>
            <h2>
              More Than Care.
              <br />
              Real Human Connection.
            </h2>
            <p>
              EldyCare helps families provide emotional wellbeing, trusted companionship and meaningful social engagement for elders through carefully verified companions.
            </p>
            <button className="premium-btn" type="button" onClick={openModal}>
              Get the app
            </button>
          </div>

          <div className="combined-right-track" ref={combinedTrackRef}>
            <div className="card-stack">
              <div className="combined-card card-1" ref={(el) => { combinedCardsRef.current[0] = el; }}>
                <span className="num">01</span>
                <h3>Verified Companions</h3>
                <p>Background checked and trained students.</p>
              </div>
              <div className="combined-card card-2" ref={(el) => { combinedCardsRef.current[1] = el; }}>
                <span className="num">02</span>
                <h3>Flexible Hourly Booking</h3>
                <p>Easy scheduling based on family needs.</p>
              </div>
              <div className="combined-card card-3" ref={(el) => { combinedCardsRef.current[2] = el; }}>
                <span className="num">03</span>
                <h3>Emotional Wellbeing</h3>
                <p>Focused on meaningful companionship and connection.</p>
              </div>
              <div className="combined-card card-4" ref={(el) => { combinedCardsRef.current[3] = el; }}>
                <span className="num">04</span>
                <h3>Safe & Trusted Platform</h3>
                <p>Built with family transparency and support in mind.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section" id="contact">
        <h2>Need Someone for Your Loved Ones?</h2>
        <p>Companionship is booked in the EldyCare app. Talk to us anytime.</p>
        <div className="contact-links">
          <a href="mailto:info@eldykare.com">info@eldykare.com</a>
          <a href="mailto:support@eldykare.com">support@eldykare.com</a>
          <a href="tel:+918595729839">8595729839</a>
          <a href="https://wa.me/918595729839" target="_blank" rel="noreferrer">
            WhatsApp
          </a>
        </div>
        <div className="cta-actions">
          <button className="btn-primary" type="button" onClick={openModal}>
            Notify me when the app launches
          </button>
          <button className="btn-outline" type="button" onClick={openModal}>
            Coming soon
          </button>
        </div>
        <div className="store-badges">
          <div className="store-badge">
            <span>Download on the</span>
            <strong>App Store</strong>
            <em>Coming soon</em>
          </div>
          <div className="store-badge">
            <span>Get it on</span>
            <strong>Google Play</strong>
            <em>Coming soon</em>
          </div>
        </div>
      </section>

      <Footer onServices={openDownload} />
      <ComingSoonModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <DownloadAppModal open={downloadOpen} onClose={() => setDownloadOpen(false)} />
    </div>
  );
};

export default ElderHomePage;
